// React imports
import React, { useState, type JSX } from "react";
// Import typed Redux hooks
import { useAppDispatch } from "./hooks/reduxHooks";

// Import child components
import MobileDealsView from "./components/deals/MobileDealsView";
import DesktopDealsView from "./components/deals/DesktopDealsView";
import WorkspaceTabContent from "./components/deals/WorkspaceTabContent";
import type { WorkspaceTabBarProps } from "./components/deals/WorkspaceTabBar";

// Import workspace hooks and Redux actions
import { useChatPanelResize } from "./hooks/useChatPanelResize";
import { useWorkspaceEntrance } from "./hooks/useWorkspaceEntrance";
import { useWorkspaceTabs } from "./hooks/useWorkspaceTabs";
import { useWorkspaceClosers } from "./hooks/useWorkspaceClosers";
import { useDealSelection, useWorkspaceResults } from "./hooks/useWorkspaceSelection";
import { setSelectedDealTab } from "./DealsTableSlice_Store";

/** Count badge value for a base tab. */
function tabCount(tab: string, dealCount: number, documentCount: number): number {
  if (tab === "Deals") return dealCount;
  if (tab === "Docs") return documentCount;
  return 0;
}

// Default export of DealsLayout_Component
export default function DealsLayout_Component(): JSX.Element {
  const dispatch = useAppDispatch();
  // Mobile view state - true for chat, false for results
  const [showChat, setShowChat] = useState<boolean>(true);

  const resize = useChatPanelResize();
  const deal = useDealSelection();
  const results = useWorkspaceResults(deal.inlineDealsFlag, deal.hasDealSelected);
  const entrance = useWorkspaceEntrance(
    results.hasResults,
    results.layoutMode,
    resize.splitLayoutRef,
    // On mobile, flip back to the Chat view for a new conversation
    () => setShowChat(true)
  );

  // On mobile, flip to the results view whenever a deal or preview opens
  const tabState = useWorkspaceTabs(deal, results, () => setShowChat(false));
  const closers = useWorkspaceClosers(deal, results, tabState);

  const tabs: WorkspaceTabBarProps = {
    hasDealSelected: deal.hasDealSelected,
    dealName: deal.dealName,
    dealDocsCount: deal.dealDocsCount,
    activeTab: tabState.activeTab,
    baseTabs: tabState.baseTabs,
    documentTabs: tabState.documentTabs,
    getTabCount: (tab) => tabCount(tab, results.dealCount, results.documentCount),
    onSelectTab: tabState.setActiveTab,
    onSelectDealTab: (tab) => {
      dispatch(setSelectedDealTab(tab));
      tabState.setActiveTab(tab === "docs" ? "DealDocs" : "DealSummary");
    },
    onCloseDeal: closers.closeDeal,
    onCloseDocument: closers.closeDocument,
  };

  const content = (
    <WorkspaceTabContent
      activeTab={tabState.activeTab}
      documentTabs={tabState.documentTabs}
      onCloseDocument={closers.closeDocument}
    />
  );

  return (
    <div className="w-full h-full bg-transparent">
      <MobileDealsView
        showChat={showChat}
        onSelectView={setShowChat}
        tabs={tabs}
        content={content}
      />
      <DesktopDealsView
        showWorkspace={results.showWorkspace}
        resize={resize}
        entrance={entrance}
        inlineDealsFlag={deal.inlineDealsFlag}
        onClosePanel={closers.closePanel}
        tabs={tabs}
        content={content}
      />
    </div>
  );
}
